/**
 * Reactive store — typed event emitter for UI state management.
 */

import type { InputFormat, OutputFormat, Direction, ColorPalette } from '../core/types';

export interface AppState {
  input: string;
  inputFormat: InputFormat;
  outputFormat: OutputFormat;
  direction: Direction;
  colors: ColorPalette;
  view: 'code' | 'diagram';
  dark: boolean;
  output: string;
  error: string | null;
  editorMode: 'mermaid' | 'xml';
  previewMode: 'mermaid' | 'xml';
}

type Listener = (state: AppState) => void;

const DEFAULT_STATE: AppState = {
  input: `flowchart TD
  A([Submit Request]) --> B[Review Details]
  B --> C{Budget Approved?}
  C -- No --> D[Return to Requester]
  D --> B
  C -- Yes --> E[Send to Vendor]
  E --> F[Receive Goods]
  F --> G{Quality Check Passed?}
  G -- No --> H[File Rejection Report]
  H --> I([Close Request])
  G -- Yes --> J[Process Payment]
  J --> I`,
  inputFormat: 'flowchart',
  outputFormat: 'drawio-activity',
  direction: 'auto',
  colors: { colors: [], opacity: 1 },
  view: 'code',
  dark: false,
  output: '',
  error: null,
  editorMode: 'mermaid',
  previewMode: 'xml',
};

class Store {
  private state: AppState;
  private listeners = new Set<Listener>();

  constructor() {
    this.state = { ...DEFAULT_STATE };
  }

  get(): AppState {
    return this.state;
  }

  set(partial: Partial<AppState>): void {
    this.state = { ...this.state, ...partial };
    this.notify();
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private notify(): void {
    for (const fn of this.listeners) {
      fn(this.state);
    }
  }
}

export const store = new Store();
